import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { ImageUpload } from '@/components/ImageUpload';
import { ImageWithRegions } from '@/components/ImageWithRegions';
import { PdfPreview } from '@/components/PdfPreview';
import { QuestionList } from '@/components/QuestionList';
import { pdfjs } from 'react-pdf';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/AuthProvider';
import { getQuestionRubricAccentColor, HIGHLIGHT_STROKE } from '@/lib/regionColors';
import { alpha } from '@mui/material/styles';
import type { GradeLevel } from '@/types/grading';
import { QUESTION_TYPE_LABELS } from '@/lib/questionTypeLabels';
import type {
  ExtractedQuestion,
  QuestionRubric,
  RubricCriterion,
  QuestionIdentifierResult,
  QuestionRegion,
} from '@/types/pipeline';
import { processQuestionIdentifier } from '@/services/questionIdentifierService';
import { suggestRubricsBatch, suggestSingleRubric } from '@/services/rubricService';

const STEPS = ['上傳試卷', '偵測題目', '定義評分標準', '發佈作業'];

// Detection model is governed server-side by the admin panel (模型設定 →
// 題目偵測). Set VITE_DETECTION_* only to force an override for local testing.
const DETECTION_MODEL =
  import.meta.env.VITE_DETECTION_PROVIDER && import.meta.env.VITE_DETECTION_MODEL
    ? {
        provider: import.meta.env.VITE_DETECTION_PROVIDER,
        model: import.meta.env.VITE_DETECTION_MODEL,
      }
    : undefined;
// Rubric model is governed server-side by the admin panel (模型設定 →
// 評分準則建議). Set VITE_RUBRIC_* only to force an override for local testing.
const RUBRIC_MODEL =
  import.meta.env.VITE_RUBRIC_PROVIDER && import.meta.env.VITE_RUBRIC_MODEL
    ? {
        provider: import.meta.env.VITE_RUBRIC_PROVIDER,
        model: import.meta.env.VITE_RUBRIC_MODEL,
      }
    : undefined;
const gradeLevels: GradeLevel[] = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'S1', 'S2', 'S3', 'S4', 'S5', 'S6'];

export function NewAssignmentPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get('id');

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!resumeId);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  // Step 1 state
  const [imagePaths, setImagePaths] = useState<string[]>([]);
  const [gradeLevel, setGradeLevel] = useState<GradeLevel>('P4');
  const [assignmentId, setAssignmentId] = useState<string | null>(resumeId);

  // Step 2 state
  const [questionResult, setQuestionResult] = useState<QuestionIdentifierResult | null>(null);
  const [extractedQuestions, setExtractedQuestions] = useState<ExtractedQuestion[]>([]);
  const [detectionImages, setDetectionImages] = useState<string[]>([]);
  const [detectionPageIndex, setDetectionPageIndex] = useState(0);
  const [detectionFocusedId, setDetectionFocusedId] = useState<string | null>(null);

  // Step 3 state
  const [rubrics, setRubrics] = useState<QuestionRubric[]>([]);
  const [rubricPageIndex, setRubricPageIndex] = useState(0);
  const [focusedQuestionId, setFocusedQuestionId] = useState<string | null>(null);
  // Tracks which questions had their AI-detected type manually overridden (session only)
  const [manuallyChangedTypes, setManuallyChangedTypes] = useState<Set<string>>(new Set());

  // Step 4 state
  const [submitCode, setSubmitCode] = useState<string | null>(null);

  // Convert uploaded Supabase storage paths to data URLs for the vision API
  const [imageDataUrls, setImageDataUrls] = useState<string[]>([]);

  useEffect(() => {
    if (imagePaths.length === 0) {
      setImageDataUrls([]);
      return;
    }
    const urls = imagePaths.map((path) => {
      const { data } = supabase.storage.from('assignment-images').getPublicUrl(path);
      return data.publicUrl;
    });
    setImageDataUrls(urls);
  }, [imagePaths]);

  // --- Auto-save wizard state ---
  const saveWizardState = useCallback(async (step: number, data?: Record<string, unknown>) => {
    if (!assignmentId || !user) return;
    try {
      await fetch(`/api/assignments/${assignmentId}/wizard`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-teacher-id': user.id },
        body: JSON.stringify({
          wizardStep: step,
          wizardData: data || {},
        }),
      });
    } catch (err) {
      console.warn('Auto-save failed:', err);
    }
  }, [assignmentId, user]);

  // --- Resume draft on mount ---
  useEffect(() => {
    if (!resumeId || !user) return;

    const loadDraft = async () => {
      try {
        const response = await fetch(`/api/assignments/${resumeId}`, {
          headers: { 'x-teacher-id': user.id },
        });
        if (!response.ok) {
          setError('無法載入草稿');
          setInitialLoading(false);
          return;
        }
        const { assignment } = await response.json();

        // Restore Step 1 state — handle both raw storage paths and legacy full URLs
        const rawPaths = (assignment.image_paths || []).map((p: string) => {
          const marker = '/assignment-images/';
          const idx = p.indexOf(marker);
          return idx !== -1 ? p.substring(idx + marker.length) : p;
        });
        setImagePaths(rawPaths);
        setGradeLevel(assignment.grade_level || 'P4');
        setAssignmentId(assignment.id);

        // Restore wizard data
        const wd = assignment.wizard_data || {};
        const step = assignment.wizard_step ?? 0;

        if (wd.questionResult) {
          setQuestionResult(wd.questionResult);
          setExtractedQuestions(wd.extractedQuestions || []);
        }
        if (wd.rubrics) {
          setRubrics(wd.rubrics);
        }

        setActiveStep(step);
      } catch (err) {
        setError('載入草稿失敗');
      } finally {
        setInitialLoading(false);
      }
    };

    loadDraft();
  }, [resumeId, user]);

  const getAuthHeaders = async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    return {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
      'x-teacher-id': user?.id || '',
    };
  };

  // --- Step 1: Create assignment ---
  const handleCreateAssignment = async () => {
    if (imagePaths.length === 0) {
      setError('請上傳至少一張試卷圖片');
      return;
    }
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-teacher-id': user.id },
        body: JSON.stringify({
          imagePaths,
          gradeLevel,
          questionType: 'mixed',
          highStakes: false,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '建立作業失敗');
      }

      const { assignment } = await response.json();
      setAssignmentId(assignment.id);
      setActiveStep(1);
      // Auto-save: assignment just created as draft at step 1
      await saveWizardState(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : '建立作業時發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  const MAX_IMAGE_DIMENSION = 2048;

  // Resize an image data URL to fit within max dimensions (keeps aspect ratio)
  const resizeImage = (dataUrl: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (img.width <= MAX_IMAGE_DIMENSION && img.height <= MAX_IMAGE_DIMENSION) {
          resolve(dataUrl);
          return;
        }
        const scale = Math.min(MAX_IMAGE_DIMENSION / img.width, MAX_IMAGE_DIMENSION / img.height);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = dataUrl;
    });
  };

  // Convert a PDF URL to an array of page image data URLs
  const pdfToImages = async (pdfUrl: string): Promise<string[]> => {
    const pdf = await pdfjs.getDocument(pdfUrl).promise;
    const images: string[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1 });
      const scale = Math.min(MAX_IMAGE_DIMENSION / viewport.width, MAX_IMAGE_DIMENSION / viewport.height, 2);
      const scaledViewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      const ctx = canvas.getContext('2d')!;
      await page.render({ canvasContext: ctx, viewport: scaledViewport, canvas } as any).promise;
      images.push(canvas.toDataURL('image/jpeg', 0.85));
    }
    return images;
  };

  // --- Step 2: Detect questions ---
  const handleDetectQuestions = async () => {
    if (imagePaths.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      // Convert all uploads to base64 data URLs, resized to keep payloads manageable
      const allImages: string[] = [];
      for (let i = 0; i < imagePaths.length; i++) {
        const path = imagePaths[i] ?? '';
        const url = imageDataUrls[i];
        if (!url) continue;

        if (path.toLowerCase().endsWith('.pdf')) {
          setProgress('正在轉換 PDF 頁面為圖片…');
          const pageImages = await pdfToImages(url);
          allImages.push(...pageImages);
        } else {
          setProgress('正在載入圖片…');
          const resp = await fetch(url);
          const blob = await resp.blob();
          const rawDataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          const resized = await resizeImage(rawDataUrl);
          allImages.push(resized);
        }
      }
      setError(null);

      if (allImages.length === 0) {
        setError('無法取得圖片');
        return;
      }

      setDetectionImages(allImages);
      setDetectionPageIndex(0);

      // Use the shared question identifier service
      const { questionResult: mergedResult, extractedQuestions: extracted } = await processQuestionIdentifier(
        allImages,
        DETECTION_MODEL,
        (_current, _total, message) => setProgress(message)
      );

      setQuestionResult(mergedResult);
      setExtractedQuestions(extracted);
      setManuallyChangedTypes(new Set());

      // Save detection results (not images — those are regenerated on demand)
      if (assignmentId) {
        await saveWizardState(1, { questionResult: mergedResult, extractedQuestions: extracted });
      }

      setError(null);

      if (mergedResult.regions.length === 0) {
        setError('未偵測到任何題目區域，請確認圖片清晰度後重試。');
      } else if (extracted.length === 0) {
        setError(`偵測到 ${mergedResult.regions.length} 個區域，但未能配對題目與作答。請確認試卷包含學生作答內容。`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '偵測題目失敗');
    } finally {
      setProgress(null);
      setLoading(false);
    }
  };

  // --- Step 3: Suggest rubrics ---
  const handleSuggestRubrics = async () => {
    if (extractedQuestions.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const allRubrics = await suggestRubricsBatch(
        extractedQuestions,
        gradeLevel,
        RUBRIC_MODEL,
        (_batch, _total, message) => setProgress(message),
      );
      setRubrics(allRubrics);
      await saveWizardState(2, { questionResult, extractedQuestions, rubrics: allRubrics });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : '生成評分標準失敗');
    } finally {
      setProgress(null);
      setLoading(false);
    }
  };

  // --- Step 3b: Suggest rubric for a single question ---
  const [suggestingQuestion, setSuggestingQuestion] = useState<string | null>(null);

  const handleSuggestSingleRubric = async (question: ExtractedQuestion) => {
    setSuggestingQuestion(question.questionId);
    try {
      const suggested = await suggestSingleRubric(question, gradeLevel, RUBRIC_MODEL);
      if (suggested) {
        updateRubric(question.questionId, {
          gradingMode: suggested.gradingMode,
          maxMarks: suggested.maxMarks,
          modelAnswer: suggested.modelAnswer,
          criteria: suggested.criteria,
          notes: suggested.notes,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '生成評分標準失敗');
    } finally {
      setSuggestingQuestion(null);
    }
  };

  // --- Step 4: Finalize assignment ---
  const handleFinalize = async () => {
    if (!assignmentId || !user) return;

    setLoading(true);
    setError(null);

    try {
      // Save rubrics first
      await saveWizardState(3, { questionResult, extractedQuestions, rubrics });

      const response = await fetch(`/api/assignments/${assignmentId}/finalize`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-teacher-id': user.id },
      });

      if (!response.ok) throw new Error(await response.text());

      const data = await response.json();
      setSubmitCode(data.submitCode);
    } catch (err) {
      setError(err instanceof Error ? err.message : '發佈作業失敗');
    } finally {
      setLoading(false);
    }
  };

  // --- Question type override ---
  const updateExtractedQuestionType = (questionId: string, newType: ExtractedQuestion['questionType']) => {
    setExtractedQuestions((prev) =>
      prev.map((q) => (q.questionId === questionId ? { ...q, questionType: newType } : q)),
    );
    setManuallyChangedTypes((prev) => new Set([...prev, questionId]));
  };

  const updateExtractedQuestion = (questionId: string, patch: Partial<ExtractedQuestion>) => {
    setExtractedQuestions((prev) =>
      prev.map((q) => (q.questionId === questionId ? { ...q, ...patch } : q)),
    );
  };

  // --- Rubric editing helpers ---
  const updateRubric = (questionId: string, patch: Partial<Omit<QuestionRubric, 'questionId'>>) => {
    setRubrics((prev) => {
      const idx = prev.findIndex((r) => r.questionId === questionId);
      if (idx === -1) {
        return [...prev, { questionId, gradingMode: 'auto', maxMarks: 1, ...patch } as QuestionRubric];
      }
      const updated = [...prev];
      updated[idx] = { ...prev[idx], ...patch, questionId } as QuestionRubric;
      return updated;
    });
  };

  const updateCriterion = (questionId: string, criterionId: string, patch: Partial<RubricCriterion>) => {
    setRubrics((prev) => {
      const rubric = prev.find((r) => r.questionId === questionId);
      if (!rubric?.criteria) return prev;
      const updatedCriteria = rubric.criteria.map((c) => (c.id === criterionId ? { ...c, ...patch } : c));
      const idx = prev.indexOf(rubric);
      const updated = [...prev];
      updated[idx] = { ...rubric, criteria: updatedCriteria, maxMarks: updatedCriteria.reduce((s, c) => s + c.points, 0) };
      return updated;
    });
  };

  const addCriterion = (questionId: string) => {
    setRubrics((prev) => {
      const rubric = prev.find((r) => r.questionId === questionId);
      const criteria = rubric?.criteria || [];
      const newId = `c${criteria.length + 1}`;
      const updated = [...criteria, { id: newId, description: '', points: 1 }];
      const idx = prev.findIndex((r) => r.questionId === questionId);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...prev[idx]!, criteria: updated, maxMarks: updated.reduce((s, c) => s + c.points, 0) };
      return next;
    });
  };

  const removeCriterion = (questionId: string, criterionId: string) => {
    setRubrics((prev) => {
      const rubric = prev.find((r) => r.questionId === questionId);
      if (!rubric?.criteria) return prev;
      const updated = rubric.criteria.filter((c) => c.id !== criterionId);
      const idx = prev.indexOf(rubric);
      const next = [...prev];
      next[idx] = { ...rubric!, criteria: updated, maxMarks: updated.reduce((s, c) => s + c.points, 0) };
      return next;
    });
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8, gap: 2 }}>
        <CircularProgress size={32} />
        <Typography color="text.secondary">正在載入草稿…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {resumeId ? '繼續編輯作業' : '新增作業'}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {progress && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 3 }}>
          {progress}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ========== Step 1: Upload ========== */}
      {activeStep === 0 && (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h6">上傳試卷圖片</Typography>
                <Tooltip title="即將推出">
                  <span>
                    <Button variant="outlined" disabled startIcon={<CloudDownloadIcon />} size="small">
                      從 Google Classroom 匯入
                    </Button>
                  </span>
                </Tooltip>
              </Stack>

              <ImageUpload value={imagePaths} onChange={setImagePaths} maxFiles={5} />

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>年級</InputLabel>
                <Select value={gradeLevel} label="年級" onChange={(e) => setGradeLevel(e.target.value as GradeLevel)}>
                  {gradeLevels.map((level) => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={2}>
                <Button variant="contained" size="large" disabled={loading || imagePaths.length === 0} onClick={handleCreateAssignment}>
                  {loading ? <CircularProgress size={24} /> : '下一步：偵測題目'}
                </Button>
                <Button variant="outlined" size="large" onClick={() => navigate('/assignments')}>
                  取消
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ========== Step 2: Detect Questions ========== */}
      {activeStep === 1 && (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6">偵測題目區域與辨識作答內容</Typography>

              {!questionResult ? (
                <>
                  {imagePaths.length > 0 && !loading && (
                    <Stack spacing={2}>
                      {imagePaths.map((path, i) => {
                        const isPdf = path.toLowerCase().endsWith('.pdf');
                        const url = imageDataUrls[i];
                        if (!url) return null;
                        if (isPdf) return <PdfPreview key={path} url={url} />;
                        return (
                          <Box key={path} component="img" src={url} sx={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                        );
                      })}
                    </Stack>
                  )}
                  {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
                      <CircularProgress size={32} />
                      <Typography color="text.secondary">正在偵測題目區域…</Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0, maxWidth: 600 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">偵測結果（區域標記）</Typography>
                      {detectionImages.length > 1 && (
                        <>
                          <IconButton size="small" disabled={detectionPageIndex <= 0} onClick={() => setDetectionPageIndex((p) => p - 1)}>
                            <ArrowBackIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="body2" color="text.secondary">
                            第 {detectionPageIndex + 1} / {detectionImages.length} 頁
                          </Typography>
                          <IconButton size="small" disabled={detectionPageIndex >= detectionImages.length - 1} onClick={() => setDetectionPageIndex((p) => p + 1)}>
                            <ArrowForwardIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Stack>
                    {detectionImages[detectionPageIndex] && (
                      <ImageWithRegions
                        src={detectionImages[detectionPageIndex]}
                        regions={questionResult.regions.filter((r) => (r.pageIndex ?? 0) === detectionPageIndex)}
                        highlightIndex={
                          detectionFocusedId != null
                            ? questionResult.regions
                                .filter((r) => (r.pageIndex ?? 0) === detectionPageIndex)
                                .findIndex((r) => r.questionId === detectionFocusedId)
                            : undefined
                        }
                        onRegionHoverEnter={(questionId) => {
                          setDetectionFocusedId(questionId);
                        }}
                        onRegionHoverLeave={() => {
                          setDetectionFocusedId(null);
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      偵測到 {extractedQuestions.length} 道題目
                    </Typography>
                    <QuestionList
                      questions={extractedQuestions}
                      allRegions={questionResult.regions}
                      focusedQuestionId={detectionFocusedId}
                      onQuestionHoverEnter={(questionId) => {
                        setDetectionFocusedId(questionId);
                        const region = questionResult.regions.find(
                          (r) =>
                            r.questionId === questionId &&
                            (r.type === 'question' || r.type === 'mc-question')
                        ) || questionResult.regions.find(
                          (r) =>
                            r.parentQuestion === questionId &&
                            (r.type?.startsWith('answer-') ?? false)
                        );
                        if (region && (region.pageIndex ?? 0) !== detectionPageIndex) {
                          setDetectionPageIndex(region.pageIndex ?? 0);
                        }
                      }}
                      onQuestionHoverLeave={() => setDetectionFocusedId(null)}
                      maxHeight={500}
                      onQuestionTypeChange={updateExtractedQuestionType}
                      manuallyChangedIds={manuallyChangedTypes}
                      onQuestionContentChange={(questionId, content) => {
                        updateExtractedQuestion(questionId, { questionText: content });
                      }}
                    />
                  </Box>
                </Stack>
              )}

              <Stack direction="row" spacing={2}>
                {!questionResult ? (
                  <Button variant="contained" size="large" disabled={loading} onClick={handleDetectQuestions}>
                    {loading ? <CircularProgress size={24} /> : '開始偵測'}
                  </Button>
                ) : (
                  <>
                    {(() => {
                      const hasEmptyEssay = extractedQuestions.some(
                        (q) => q.questionType === 'essay' && !q.questionText.trim(),
                      );
                      return (
                        <Stack spacing={0.5}>
                          <Button
                            variant="contained"
                            size="large"
                            disabled={extractedQuestions.length === 0 || hasEmptyEssay}
                            onClick={() => { setActiveStep(2); saveWizardState(2, { questionResult, extractedQuestions }); }}
                          >
                            下一步：定義評分標準
                          </Button>
                          {hasEmptyEssay && (
                            <Typography variant="caption" color="warning.main">
                              請先填寫作文題目內容
                            </Typography>
                          )}
                        </Stack>
                      );
                    })()}
                    <Button variant="outlined" onClick={handleDetectQuestions} disabled={loading}>
                      {loading ? <CircularProgress size={20} /> : '重新偵測'}
                    </Button>
                  </>
                )}
                <Button variant="text" onClick={() => setActiveStep(0)}>上一步</Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ========== Step 3: Rubric Editor ========== */}
      {activeStep === 2 && (() => {
        const pageRegions = questionResult?.regions.filter((r) => (r.pageIndex ?? 0) === rubricPageIndex) || [];
        const highlightIdx = focusedQuestionId != null
          ? pageRegions.findIndex((r) => r.questionId === focusedQuestionId)
          : undefined;

        return (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6">定義評分標準</Typography>
                  <Button variant="outlined" onClick={handleSuggestRubrics} disabled={loading}>
                    {loading ? <CircularProgress size={20} /> : 'AI 自動建議'}
                  </Button>
                  {rubrics.length > 0 && (
                    <Button variant="text" color="warning" onClick={() => setRubrics([])}>清除全部</Button>
                  )}
                  {rubrics.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      共 {rubrics.length} 題 · 總分 {rubrics.reduce((s, r) => s + r.maxMarks, 0)}
                    </Typography>
                  )}
                </Stack>

                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
                  {/* Left: Paper image with region overlay */}
                  <Box sx={{ flex: 'none', width: { xs: '100%', lg: 420 }, position: 'sticky', top: 16 }}>
                    {detectionImages.length > 1 && (
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <IconButton size="small" disabled={rubricPageIndex <= 0} onClick={() => setRubricPageIndex((p) => p - 1)}>
                          <ArrowBackIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" color="text.secondary">
                          第 {rubricPageIndex + 1} / {detectionImages.length} 頁
                        </Typography>
                        <IconButton size="small" disabled={rubricPageIndex >= detectionImages.length - 1} onClick={() => setRubricPageIndex((p) => p + 1)}>
                          <ArrowForwardIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                    {detectionImages[rubricPageIndex] && (
                      <ImageWithRegions
                        src={detectionImages[rubricPageIndex]}
                        regions={pageRegions}
                        highlightIndex={highlightIdx !== -1 ? highlightIdx : undefined}
                        onRegionHoverEnter={(questionId) => {
                          setFocusedQuestionId(questionId);
                        }}
                        onRegionHoverLeave={() => {
                          setFocusedQuestionId(null);
                        }}
                      />
                    )}
                  </Box>

                  {/* Right: Rubric editing form */}
                  <Box sx={{ flex: 1, minWidth: 0, maxHeight: 700, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    {extractedQuestions.map((q, i) => {
                      const rubric = rubrics.find((r) => r.questionId === q.questionId);
                      const mode = rubric?.gradingMode || 'auto';
                      const isFocused = focusedQuestionId === q.questionId;
                      const regionsTyped = questionResult?.regions as QuestionRegion[] || [];
                      const boxAccent = getQuestionRubricAccentColor(regionsTyped, q.questionId);
                      const strokeColor = isFocused ? HIGHLIGHT_STROKE : boxAccent;
                      const rowBg = isFocused
                        ? alpha(HIGHLIGHT_STROKE, 0.14)
                        : boxAccent != null
                          ? alpha(boxAccent, 0.09)
                          : i % 2 === 0 ? 'background.paper' : 'grey.50';

                      return (
                        <Box
                          key={q.questionId}
                          onMouseEnter={() => {
                            setFocusedQuestionId(q.questionId);
                            const region = questionResult?.regions.find(
                              (r) =>
                                r.questionId === q.questionId &&
                                (r.type === 'question' || r.type === 'mc-question')
                            ) ||
                              questionResult?.regions.find(
                                (r) =>
                                  r.parentQuestion === q.questionId &&
                                  (r.type?.startsWith('answer-') ?? false)
                              );
                            if (region && (region.pageIndex ?? 0) !== rubricPageIndex) {
                              setRubricPageIndex(region.pageIndex ?? 0);
                            }
                          }}
                          onMouseLeave={() => setFocusedQuestionId(null)}
                          sx={{
                            p: 2,
                            pl: 1.25,
                            borderBottom: i < extractedQuestions.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            borderLeft: '4px solid',
                            borderLeftColor: strokeColor ?? 'transparent',
                            bgcolor: rowBg,
                            transition: 'background-color 0.15s, border-color 0.15s, outline 0.15s',
                            // Add red border outline when focused
                            outline: isFocused ? `2px solid ${HIGHLIGHT_STROKE}` : 'none',
                            outlineOffset: '-2px',
                          }}
                        >
                          {/* Question header with points */}
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                sx={{
                                  color: isFocused ? HIGHLIGHT_STROKE : boxAccent ?? 'primary.main',
                                }}
                              >
                                {q.questionId}
                              </Typography>
                              <FormControl size="small">
                                <Select
                                  value={q.questionType}
                                  onChange={(e) =>
                                    updateExtractedQuestionType(
                                      q.questionId,
                                      e.target.value as ExtractedQuestion['questionType'],
                                    )
                                  }
                                  sx={{
                                    fontSize: '0.8125rem',
                                    minWidth: 140,
                                    color: isFocused ? HIGHLIGHT_STROKE : boxAccent ?? undefined,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: isFocused
                                        ? HIGHLIGHT_STROKE
                                        : boxAccent ?? undefined,
                                    },
                                  }}
                                >
                                  {Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => (
                                    <MenuItem key={value} value={value} sx={{ fontSize: '0.8125rem' }}>
                                      {label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              {manuallyChangedTypes.has(q.questionId) && (
                                <Typography variant="caption" color="warning.main" sx={{ whiteSpace: 'nowrap' }}>
                                  已手動修改
                                </Typography>
                              )}
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="caption" color="text.secondary">分數</Typography>
                              <TextField
                                type="number"
                                value={rubric?.maxMarks ?? 1}
                                onChange={(e) => updateRubric(q.questionId, { maxMarks: Math.max(0, Number(e.target.value)) })}
                                size="small"
                                sx={{ width: 65 }}
                                inputProps={{ min: 0, step: 0.5 }}
                              />
                            </Stack>
                          </Stack>
                          {q.sectionInstruction && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              {q.sectionInstruction}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ mb: 1 }}>{q.questionText}</Typography>

                          {/* Per-question AI suggest + mode controls */}
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={suggestingQuestion === q.questionId}
                              onClick={() => handleSuggestSingleRubric(q)}
                            >
                              {suggestingQuestion === q.questionId ? <CircularProgress size={16} sx={{ mr: 0.5 }} /> : null}
                              AI 建議
                            </Button>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel>評分模式</InputLabel>
                              <Select
                                label="評分模式"
                                value={mode}
                                onChange={(e) => {
                                  const newMode = e.target.value as 'auto' | 'rubric';
                                  if (newMode === 'rubric' && !rubric?.criteria?.length) {
                                    updateRubric(q.questionId, { gradingMode: 'rubric', criteria: [{ id: 'c1', description: '', points: 1 }], maxMarks: rubric?.maxMarks ?? 1 });
                                  } else {
                                    updateRubric(q.questionId, { gradingMode: newMode });
                                  }
                                }}
                              >
                                <MenuItem value="auto">自動（對/錯）</MenuItem>
                                <MenuItem value="rubric">評分標準（多項）</MenuItem>
                              </Select>
                            </FormControl>
                          </Stack>

                          {/* Model answer */}
                          <TextField
                            label="參考答案"
                            value={rubric?.modelAnswer || ''}
                            onChange={(e) => updateRubric(q.questionId, { modelAnswer: e.target.value })}
                            size="small"
                            fullWidth
                            sx={{ mb: 1 }}
                          />

                          {/* Criteria list (rubric mode) */}
                          {mode === 'rubric' && rubric && (
                            <Box
                              sx={{
                                pl: 1,
                                borderLeft: '2px solid',
                                borderColor: boxAccent ?? 'secondary.light',
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                評分標準（{rubric.criteria?.length || 0} 項，共 {rubric.maxMarks} 分）
                              </Typography>
                              <Stack spacing={0.75} sx={{ mb: 1 }}>
                                {(rubric.criteria || []).map((c, ci) => (
                                  <Stack key={c.id} direction="row" spacing={1} alignItems="center">
                                    <Typography variant="caption" sx={{ width: 20, textAlign: 'center', color: 'text.secondary', flexShrink: 0 }}>{ci + 1}</Typography>
                                    <TextField value={c.description} onChange={(e) => updateCriterion(q.questionId, c.id, { description: e.target.value })} placeholder="評分標準描述" size="small" sx={{ flex: 1 }} />
                                    <TextField type="number" value={c.points} onChange={(e) => updateCriterion(q.questionId, c.id, { points: Math.max(0, Number(e.target.value)) })} size="small" sx={{ width: 65 }} inputProps={{ min: 0, step: 0.5 }} label="分" />
                                    <IconButton size="small" onClick={() => removeCriterion(q.questionId, c.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                                  </Stack>
                                ))}
                                <Button size="small" startIcon={<AddIcon />} onClick={() => addCriterion(q.questionId)} sx={{ alignSelf: 'flex-start' }}>新增標準</Button>
                              </Stack>
                            </Box>
                          )}

                          {/* Essay settings panel */}
                          {q.questionType === 'essay' && (
                            <Box sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
                              <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 1 }}>
                                作文批改設定
                              </Typography>

                              {/* 自訂評分（唯一模式） */}
                              <TextField
                                label="批改要求"
                                multiline
                                rows={5}
                                fullWidth
                                required
                                value={q.essay_custom_criteria ?? ''}
                                onChange={(e) => updateExtractedQuestion(q.questionId, { essay_custom_criteria: e.target.value })}
                                placeholder={'例如：\n• 本次是小三中文短文練習，要求150-200字\n• 請檢查錯別字、句子是否通順\n• 滿分10分，内容5分、文筆3分、錯別字扣分\n• 語氣溫和鼓勵，適合小學生閱讀'}
                                size="small"
                                sx={{ mb: 1.5 }}
                                helperText="描述 AI 如何批改（評分標準、重點、語氣等）"
                              />

                              {/* 補充指引（共用） */}
                              <TextField
                                label="作文題目補充指引（選填）"
                                multiline
                                rows={2}
                                fullWidth
                                value={q.essay_instructions ?? ''}
                                onChange={(e) => updateExtractedQuestion(q.questionId, { essay_instructions: e.target.value })}
                                placeholder="如題目已正確辨識可留空。如需補充完整題目要求請在此填寫。"
                                size="small"
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2}>
                  <Button variant="contained" size="large" disabled={rubrics.length === 0} onClick={() => { setActiveStep(3); saveWizardState(3, { questionResult, extractedQuestions, rubrics }); }}>
                    下一步：開始評分
                  </Button>
                  <Button variant="text" onClick={() => setActiveStep(1)}>上一步</Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })()}

      {/* ========== Step 4: Finalize ========== */}
      {activeStep === 3 && (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6">發佈作業</Typography>

              {!submitCode ? (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    共 {extractedQuestions.length} 道題目，已定義 {rubrics.length} 項評分標準，總分 {rubrics.reduce((s, r) => s + r.maxMarks, 0)}。
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    發佈後將產生一組 6 位數提交碼，學生可透過此碼上傳作答。
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button variant="contained" size="large" disabled={loading} onClick={handleFinalize}>
                      {loading ? <CircularProgress size={24} /> : '確認發佈'}
                    </Button>
                    <Button variant="text" onClick={() => setActiveStep(2)}>上一步</Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
                  <Typography variant="h6" color="success.main" fontWeight={700}>
                    作業已成功發佈！
                  </Typography>
                  <Box sx={{ p: 4, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      學生提交碼
                    </Typography>
                    <Typography variant="h2" fontWeight={700} letterSpacing={8} color="primary.main">
                      {submitCode}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    請將此提交碼分享給學生，學生可前往以下連結提交作業：
                  </Typography>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" fontFamily="monospace">
                      {window.location.origin}/submit
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/submit`);
                      }}
                    >
                      複製連結
                    </Button>
                  </Box>
                  <Button variant="contained" size="large" onClick={() => navigate('/assignments')}>
                    返回作業列表
                  </Button>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
